import { lazy, Suspense, useEffect, useState } from "react";
import { MotionConfig, motion, useScroll } from "motion/react";
import { ArrowDown, ArrowUpRight, Building2, KeyRound, Wifi, Volume2, Coffee, Bike, Check, Smartphone, House, TrendingUp, ShieldCheck } from "lucide-react";
import Header from "./components/Header";
import Reveal from "./components/Reveal";
import MediaGallery from "./components/MediaGallery";
import Amenities from "./components/Amenities";
import ApartmentShowcase from "./components/ApartmentShowcase";
import FloorPlans from "./components/FloorPlans";
import LocationSection from "./components/LocationSection";
import LeadForm from "./components/LeadForm";
import { projectGallery } from "./data/project";
import { project } from "./data/urban";

const RoiCalculator = lazy(() => import("./components/RoiCalculator"));

const { stats, faq } = project;
const differentialIcons = { KeyRound, Wifi, Volume2, Coffee, Bike, Building2, House, Smartphone, TrendingUp, ShieldCheck };

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" />;
}

function Hero() {
  return (
    <section className="hero" id="inicio" aria-label={project.name}>
      <img className="hero-background" src="/assets/hero-urban-tower-v1.webp" alt={`Perspectiva ilustrativa do ${project.name} ao entardecer`} fetchPriority="high" />
      <div className="hero-shade" />
      <div className="hero-inner">
        <Reveal className="hero-copy">
          <img className="hero-project-logo" src="/assets/urban-tower-logo.png" alt={project.name} />
          <p className="hero-tagline">{project.tagline}</p>
          <img className="partner-lockup" src="/assets/sanches-logo.png" alt="Sanches Incorporadora e Construtora" />
          <h1>Um novo jeito de viver<br /><strong>no {project.neighborhood}, {project.city}.</strong></h1>
          <p className="hero-description">{project.unitTypes}</p>
          <div className="hero-actions">
            <a className="button" href="#contato">Receba o material completo</a>
            <a className="button button-outline" href="#contato">Fale com um especialista</a>
          </div>
          <dl className="hero-highlights">
            {project.heroHighlights.map(([value, caption]) => <div key={value}><dt>{value}</dt><dd>{caption}</dd></div>)}
          </dl>
        </Reveal>
      </div>
      <a className="scroll-cue" href="#conceito" aria-label="Rolar para o conceito"><ArrowDown size={24} /></a>
      <span className="hero-caption">Perspectiva ilustrativa · ambientação gerada por IA</span>
    </section>
  );
}

function StickyCta() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), { threshold: 0.1 });
    observer.observe(document.getElementById("inicio"));
    return () => observer.disconnect();
  }, []);
  return <div className={`sticky-cta ${visible ? "is-visible" : ""}`} aria-hidden={!visible} inert={!visible}>
    <div className="sticky-cta__inner">
      <div className="sticky-cta__location">Lançamento em <strong>{project.city}</strong></div>
      <div className="sticky-cta__brand"><img src="/assets/urban-tower-logo.png" alt={project.name} loading="lazy" decoding="async" /></div>
      <div className="sticky-cta__actions">
        <a className="sticky-cta__whatsapp" href="https://wa.link/apw57e" target="_blank" rel="noopener noreferrer" aria-label="Falar no WhatsApp">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.518 5.276l-.999 3.648 3.97-.62zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" /></svg>
        </a>
        <a className="sticky-cta__more" href="#contato">Quero saber mais</a>
      </div>
    </div>
  </div>;
}

export default function App() {
  return <MotionConfig reducedMotion="user">
    <a className="skip-link" href="#conteudo">Pular para o conteúdo</a>
    <ScrollProgress />
    <Header />
    <main id="conteudo">
      <Hero />
      <section className="manifesto section-pad" id="conceito">
        <div className="content-grid">
          <Reveal className="manifesto-copy">
            <p className="section-label">O conceito</p>
            <h2>{project.concept.heading}</h2>
            <p className="lead">{project.concept.lead}</p>
            {project.concept.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
            <a className="text-link" href="#projeto">Conheça o projeto <ArrowUpRight size={18} /></a>
          </Reveal>
          <Reveal className="manifesto-image"><img src="/assets/concept-wide.webp" alt={`Arquitetura e espaços do ${project.name}`} loading="lazy" /><span>Imagem meramente ilustrativa</span></Reveal>
        </div>
      </section>

      <section className="project section-pad" id="projeto">
        <div className="project-layout">
          <Reveal className="project-gallery"><MediaGallery items={projectGallery} /></Reveal>
          <Reveal className="project-information">
            <p className="section-label">O projeto</p><h2>{project.projectIntro.heading}<br /><em>{project.projectIntro.emphasis}</em></h2>
            <p>{project.projectIntro.description}</p>
            <dl className="stats">{stats.map(([value, label]) => <div key={label}><dt>{value}</dt><dd>{label}</dd></div>)}</dl>
            <a className="text-link" href="#contato">Conheça todas as possibilidades <ArrowUpRight size={18} /></a>
          </Reveal>
        </div>
      </section>

      <LocationSection />

      <section className="amenities section-pad" id="areas">
        <Reveal className="section-heading centered"><p className="section-label">Lazer, bem-estar e conveniência</p><h2>Mais possibilidades.<br /><em>Todos os dias.</em></h2><p>Do trabalho à pausa. Da rotina ao encontro. Espaços preparados para acompanhar o seu dia a dia.</p></Reveal>
        <Reveal className="media-container"><Amenities /></Reveal>
        <p className="amenities-delivery-note">Perspectivas ilustrativas. Conforme o book, as áreas comuns serão entregues preparadas e com infraestrutura, sem móveis e equipamentos.</p>
      </section>

      <section className="apartments section-pad" id="apartamentos">
        <Reveal className="section-heading centered"><p className="section-label">Apartamentos</p><h2>Seu espaço.<br /><em>Seu jeito de viver.</em></h2><p>Conheça os ambientes e encontre a tipologia que faz sentido para você.</p></Reveal>
        <Reveal className="media-container"><ApartmentShowcase /></Reveal>
      </section>

      <FloorPlans />

      <section className="differentials section-pad">
        <Reveal className="section-heading centered"><p className="section-label">Diferenciais</p><h2>Inteligência em cada detalhe.</h2></Reveal>
        <div className="differential-list">{project.differentials.map(({ icon, title, description }, index) => { const Icon = differentialIcons[icon] || Building2; return <Reveal key={title} delay={index % 3 * 0.04}><article><Icon aria-hidden="true" /><h3>{title}</h3><p>{description}</p></article></Reveal>; })}</div>
      </section>

      <Suspense fallback={<section id="investimento" className="section-pad" aria-busy="true"><div className="section-heading centered"><h2>Seu dinheiro trabalhando por você.</h2><p>Carregando simulador...</p></div></section>}><RoiCalculator /></Suspense>

      <section className="builder section-pad" id="sanches">
        <div className="content-grid">
          <Reveal className="builder-identity"><p className="section-label">O incorporador</p><img src="/assets/sanches-logo.png" alt="Sanches Incorporadora e Construtora" loading="lazy" /><p>Viva e <strong>inspire.</strong></p></Reveal>
          <Reveal className="builder-copy"><h2>Propósito, impacto<br />e história.</h2><p>Criamos espaços que conectam empreendimentos à vida. Que integram tecnologia e bem-estar. Que criam formas mais inteligentes de viver.</p><p>A Sanches une tradição, solidez e inovação em uma trajetória que começou em 1994.</p><dl><div><dt>Desde 1994</dt><dd>Construindo histórias</dd></div><div><dt>+1 milhão m²</dt><dd>Construídos</dd></div><div><dt>+10 mil</dt><dd>Unidades em portfólio</dd></div></dl></Reveal>
        </div>
      </section>

      <section className="smart-living section-pad" id="linha-urban">
        <div className="content-grid">
          <Reveal className="smart-living-heading"><img className="urban-signature" src="/assets/urban-tower-logo.png" alt={project.name} loading="lazy" /><h2>Mais que um lar.<br /><em>O seu novo estilo de vida.</em></h2><p>O Urban Square faz parte da Linha Urban da Sanches: espaços para viver um novo capítulo da vida urbana.</p><a className="text-link" href="#contato">Conheça a Linha Urban <ArrowUpRight size={18} /></a></Reveal>
          <Reveal className="smart-living-list"><article><House aria-hidden="true" /><div><h3>Um espaço para chamar de seu</h3><p>Apartamentos de 1 dormitório e opções Garden para diferentes formas de viver.</p></div></article><article><Coffee aria-hidden="true" /><div><h3>Encontros e possibilidades</h3><p>Ambientes compartilhados projetados para convivência, trabalho e bem-estar.</p></div></article><article><Building2 aria-hidden="true" /><div><h3>Seu novo capítulo no Neoville</h3><p>Um endereço em Curitiba conectado ao movimento da cidade e à sua rotina.</p></div></article></Reveal>
        </div>
      </section>

      <section className="faq section-pad" id="duvidas">
        <Reveal className="section-heading centered"><p className="section-label">Saiba mais</p><h2>Alguma dúvida?</h2><p>Conheça os detalhes do seu próximo espaço.</p></Reveal>
        <div className="faq-list">{faq.map(([question, answer]) => <details key={question}><summary>{question}<span aria-hidden="true">+</span></summary><p>{answer}</p></details>)}</div>
      </section>

      <section className="contact section-pad" id="contato">
        <div className="content-grid">
          <Reveal className="contact-copy"><p className="section-label">Seu próximo passo</p><h2>Conheça o<br />{project.name}.</h2><p>{project.tagline}<br />{project.neighborhood} · {project.city}</p><ul><li><Check /> Receba o material completo</li><li><Check /> Consulte plantas e disponibilidade</li><li><Check /> Converse com um especialista</li></ul><img className="contact-project-logo" src="/assets/urban-tower-logo.png" alt={project.name} loading="lazy" /></Reveal>
          <Reveal><LeadForm /></Reveal>
        </div>
      </section>
    </main>
    <footer className="site-footer" id="rodape">
      <div className="footer-accent" aria-hidden="true" />
      <div className="footer-container">
        <div className="footer-main">
          <div className="footer-identity">
            <img src="/assets/urban-tower-logo.png" alt={project.name} loading="lazy" decoding="async" />
            <p>{project.tagline}</p>
          </div>
          <p className="footer-description">{project.footerDescription}</p>
          <nav className="footer-socials" aria-label="Redes sociais da Sanches">
            <a href="https://www.instagram.com/sanches.construtora" target="_blank" rel="noopener noreferrer" aria-label="@sanches.construtora"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></svg><span>@sanches.construtora</span></a>
            <a href="https://www.youtube.com/@ConstrutoraSanches" target="_blank" rel="noopener noreferrer" aria-label="Construtora Sanches no YouTube"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" /><path d="m10 15 5-3-5-3z" /></svg><span>Construtora Sanches</span></a>
          </nav>
        </div>
        <div className="footer-legal">
          <p>© {new Date().getFullYear()} Sanches Construtora e Incorporadora. Todos os direitos reservados.<br />{project.name} é um empreendimento da Sanches Construtora.</p>
          <p className="footer-disclaimer">As imagens e renderizações são meramente ilustrativas. Valores e condições sujeitos a alteração sem aviso prévio.</p>
        </div>
      </div>
    </footer>
    <StickyCta />
  </MotionConfig>;
}
